/**
 * Сбор файлов пакета РД и отправка по одному (docs/pto-rd-import-spec.md).
 *
 * Источники: ZIP-архив (читается по оглавлению, файл за файлом — архив в
 * память целиком не грузится; целостность — по CRC), папка (выбор или
 * перетаскивание) или отдельные файлы. Путь в архиве/папке уходит на сервер —
 * по нему программа узнаёт титул, если его нет в шифре. RAR и 7z браузер не
 * распакует — просим ZIP или папку.
 */
import { backoff, MAX_ATTEMPTS, retryable, sleep } from '@shared/lib/uploadBatches';
import { listZip, readZipEntry } from '@shared/lib/zipReader';
import { httpStatus, isAbortError, NO_WRITE_ACCESS, uploadErrorText } from './errors';
import { plural } from './format';

/** Файл пакета: путь в архиве/папке, размер, чтение по требованию. */
export interface UploadSource {
  path: string;
  size: number;
  /** Содержимое (запись архива — распаковка с проверкой CRC; прерывается `signal`). */
  open: (signal: AbortSignal) => Promise<Blob>;
}

/** Как на сервере: файл больше — не принимается. */
export const MAX_FILE_BYTES = 200 * 1024 * 1024;

const ARCHIVE = /\.(zip|rar|7z)$/i;
/** Сколько имён показывать в предупреждении, остальные — «и ещё N». */
const PREVIEW_NAMES = 5;

export function isJunk(path: string): boolean {
  const name = (path.split('/').pop() ?? '').toLowerCase();
  return (
    !name ||
    name === 'thumbs.db' ||
    name === 'desktop.ini' ||
    name === '.ds_store' ||
    name.startsWith('~$') ||
    name.startsWith('._') ||
    path.split('/').includes('__MACOSX')
  );
}

/**
 * Путь так, как его сохраняет сервер (`clean_path` в `rd_import/service.rs`):
 * «\» → «/», без пустых сегментов и «.», не длиннее 1000 символов. По нему
 * дозагрузка узнаёт уже загруженные файлы.
 */
export function cleanPath(path: string): string {
  const clean = path
    .replace(/\\/g, '/')
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s && s !== '.')
    .join('/');
  return Array.from(clean).slice(0, 1000).join('');
}

const relPath = (f: File): string =>
  ((f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name).replace(/\\/g, '/');

/** Файлы перетаскивания: папки обходим рекурсивно (путь — от брошенной папки). */
export async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  // Всё из DataTransfer читаем синхронно — после события он пустеет.
  const entries = Array.from(dt.items ?? [])
    .map((i) => i.webkitGetAsEntry?.())
    .filter((e): e is FileSystemEntry => Boolean(e));
  if (entries.length === 0) return Array.from(dt.files ?? []);
  const out: File[] = [];
  const walk = async (entry: FileSystemEntry): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject),
      );
      const path = entry.fullPath.replace(/^\/+/, '');
      out.push(new File([file], path, { type: file.type, lastModified: file.lastModified }));
      return;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    // readEntries отдаёт папку порциями (в Chrome — по 100) до пустого ответа.
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      );
      if (batch.length === 0) break;
      for (const child of batch) await walk(child);
    }
  };
  for (const e of entries) await walk(e);
  return out;
}

function names(list: string[]): string {
  const shown = list
    .slice(0, PREVIEW_NAMES)
    .map((n) => `«${n}»`)
    .join(', ');
  return list.length > PREVIEW_NAMES ? `${shown} и ещё ${list.length - PREVIEW_NAMES}` : shown;
}

/** Источники пакета из выбранных файлов, имя пакета и предупреждения (что не вошло и почему). */
export async function collectSources(
  files: File[],
): Promise<{ sources: UploadSource[]; name: string; warnings: string[] }> {
  const sources: UploadSource[] = [];
  const warnings: string[] = [];
  const big: string[] = [];
  const empty: string[] = [];
  const encrypted: string[] = [];
  const nested: string[] = [];
  const unsupported: string[] = [];
  const rar: string[] = [];
  let junk = 0;
  const add = (path: string, size: number, open: UploadSource['open']) => {
    if (isJunk(path)) junk += 1;
    else if (size === 0) empty.push(path);
    else if (size > MAX_FILE_BYTES) big.push(path);
    else sources.push({ path, size, open });
  };
  const several = files.length > 1;
  for (const file of files) {
    const rel = relPath(file);
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.zip')) {
      // Несколько архивов или архив внутри папки — пути записей с префиксом
      // архива: одинаковые пути в разных архивах не заменят друг друга, а имя
      // архива («101.zip») подскажет титул.
      const prefix = several || rel.includes('/') ? `${rel.replace(/\.zip$/i, '')}/` : '';
      try {
        for (const entry of await listZip(file)) {
          const path = prefix + entry.path;
          if (isJunk(path)) junk += 1;
          else if (entry.encrypted) encrypted.push(path);
          else if (ARCHIVE.test(entry.path)) nested.push(path);
          else if (entry.method !== 0 && entry.method !== 8) unsupported.push(path);
          else add(path, entry.size, (signal) => readZipEntry(file, entry, { signal }));
        }
      } catch (e) {
        warnings.push(`Архив «${rel}» не читается: ${(e as Error).message}.`);
      }
    } else if (lower.endsWith('.rar') || lower.endsWith('.7z')) {
      rar.push(rel);
    } else {
      add(rel, file.size, async () => file);
    }
  }
  if (rar.length > 0) {
    warnings.push(
      `RAR и 7z в браузере не распаковываются — упакуйте в ZIP или выберите папку: ${names(rar)}.`,
    );
  }
  if (nested.length > 0) {
    warnings.push(
      `Вложенный архив — распакуйте и загрузите папкой (${nested.length}): ${names(nested)}.`,
    );
  }
  if (big.length > 0) {
    warnings.push(`Больше 200 МБ — не загружаются (${big.length}): ${names(big)}.`);
  }
  if (empty.length > 0) {
    warnings.push(`Пустые файлы пропущены (${empty.length}): ${names(empty)}.`);
  }
  if (encrypted.length > 0) {
    warnings.push(
      `Зашифрованы в архиве — пропущены (${encrypted.length}): ${names(encrypted)}. ` +
        'Упакуйте без пароля.',
    );
  }
  if (unsupported.length > 0) {
    warnings.push(
      `Сжаты неподдерживаемым методом — пропущены (${unsupported.length}): ${names(unsupported)}. ` +
        'Перепакуйте в обычный ZIP или выберите папку.',
    );
  }
  if (junk > 0) warnings.push(`Служебные файлы (Thumbs.db и т. п.) пропущены: ${junk}.`);
  const first = files[0];
  const folder = first ? relPath(first).split('/')[0] : '';
  const name =
    files.length === 1 && first && (ARCHIVE.test(first.name) || !relPath(first).includes('/'))
      ? first.name
      : folder && files.every((f) => relPath(f).startsWith(`${folder}/`))
        ? folder
        : `${sources.length} ${plural(sources.length, ['файл', 'файла', 'файлов'])}`;
  return { sources, name, warnings };
}

export interface UploadProgress {
  /** Обработано файлов (отправлены или не ушли). */
  done: number;
  total: number;
  /** Сколько из обработанных не ушло. */
  failed: number;
  bytesDone: number;
  bytesTotal: number;
  current: string | null;
}

/** Файл, который не ушёл на сервер, и причина. */
export interface UploadFailure {
  path: string;
  reason: string;
}

export interface UploadResult {
  uploaded: number;
  failed: UploadFailure[];
  /** Почему отправка остановлена целиком (пакет удалён, не принимает файлы, нет прав). */
  stopped: string | null;
  /** HTTP-код остановки: 404 — пакета больше нет. */
  stopStatus: number | null;
  /** Сколько файлов не отправлялось: остановка или «Прервать». */
  notSent: number;
  aborted: boolean;
}

/** Отправка одного файла; `signal` отменяет текущий запрос. */
export type SendFile = (path: string, blob: Blob, signal: AbortSignal) => Promise<void>;

/** Итог загрузки — уходит в карточку пакета через состояние навигации. */
export interface UploadReport {
  uploaded: number;
  /** Уже были на сервере (тот же путь и размер) — не отправлялись. */
  skippedExisting: number;
  failed: UploadFailure[];
  notSent: number;
  aborted: boolean;
  stopped: string | null;
  /** Что не вошло в пакет при сборе файлов и почему. */
  warnings: string[];
  /** Файлы загружены, но пакет не встал в очередь распознавания. */
  recognizeError: string | null;
  /** Загрузка оборвалась ошибкой не по конкретному файлу. */
  error: string | null;
}

/** Ключ отчёта о загрузке в `location.state` карточки пакета. */
export const REPORT_STATE_KEY = 'rdUploadReport';

/** Отчёт о загрузке из `location.state` (или null, если его там нет). */
export function reportFromState(state: unknown): UploadReport | null {
  const r = (state as Record<string, unknown> | null | undefined)?.[REPORT_STATE_KEY] as
    Partial<UploadReport> | undefined;
  if (!r || typeof r !== 'object' || !Array.isArray(r.failed) || !Array.isArray(r.warnings)) {
    return null;
  }
  return {
    uploaded: r.uploaded ?? 0,
    skippedExisting: r.skippedExisting ?? 0,
    failed: r.failed,
    notSent: r.notSent ?? 0,
    aborted: Boolean(r.aborted),
    stopped: r.stopped ?? null,
    warnings: r.warnings,
    recognizeError: r.recognizeError ?? null,
    error: r.error ?? null,
  };
}

/** Сбой, после которого остальные файлы слать бессмысленно. */
const STOP_REASON: Record<number, string> = {
  401: 'Сессия истекла — войдите в систему заново и дозагрузите пакет.',
  403: NO_WRITE_ACCESS,
  404: 'Пакет не найден — возможно, его удалили.',
  409: 'Пакет больше не принимает файлы: он уже отправлен на распознавание.',
};

/**
 * Отправить файлы по одному. Повтор (до трёх попыток, с паузой) — только при
 * сбое связи, 5xx, 408 и 429; 400/413 — файл пропускается сразу; 401/403/404/409 —
 * отправка останавливается (пакета нет, он не принимает файлы или нет прав).
 * «Прервать» (`signal`) отменяет текущий запрос и чтение архива.
 */
export async function uploadSources(
  sources: UploadSource[],
  send: SendFile,
  onProgress: (p: UploadProgress) => void,
  signal: AbortSignal,
): Promise<UploadResult> {
  const failed: UploadFailure[] = [];
  const bytesTotal = sources.reduce((s, x) => s + x.size, 0);
  let bytesDone = 0;
  let uploaded = 0;
  let processed = 0;
  let stopped: string | null = null;
  let stopStatus: number | null = null;
  files: for (const s of sources) {
    if (signal.aborted) break;
    onProgress({
      done: processed,
      total: sources.length,
      failed: failed.length,
      bytesDone,
      bytesTotal,
      current: s.path,
    });
    let blob: Blob | null = null;
    try {
      blob = await s.open(signal);
    } catch (e) {
      if (signal.aborted || isAbortError(e)) break;
      failed.push({
        path: s.path,
        reason: e instanceof Error && e.message ? e.message : 'не удалось прочитать файл',
      });
    }
    for (let attempt = 1; blob; attempt++) {
      try {
        await send(s.path, blob, signal);
        uploaded += 1;
        break;
      } catch (e) {
        if (signal.aborted) break files;
        const status = httpStatus(e);
        if (retryable(status) && attempt < MAX_ATTEMPTS) {
          await sleep(backoff(status, attempt), signal);
          if (signal.aborted) break files;
          continue;
        }
        failed.push({ path: s.path, reason: uploadErrorText(e) });
        if (typeof status === 'number' && STOP_REASON[status]) {
          stopped = STOP_REASON[status];
          stopStatus = status;
          processed += 1;
          break files;
        }
        break;
      }
    }
    processed += 1;
    bytesDone += s.size;
  }
  onProgress({
    done: processed,
    total: sources.length,
    failed: failed.length,
    bytesDone,
    bytesTotal,
    current: null,
  });
  return {
    uploaded,
    failed,
    stopped,
    stopStatus,
    notSent: sources.length - processed,
    aborted: signal.aborted,
  };
}
